/**
 * Client for /api/v1/me/leave-draft — single Apply-page draft per user,
 * persisted server-side. Replaces the localStorage path (which stays as
 * a transparent offline fallback when the API is unreachable).
 *
 * Single-draft rule is enforced by the partial unique index on
 * `leaves(employee_id) WHERE status='draft'` (migration 0070). PUT is an
 * upsert: the API atomically deletes the existing draft and inserts the
 * new one inside the same transaction.
 */

import { z } from 'zod'
import type { LeaveTypeId } from '@lms/shared-types'
import { docRefSchema } from '@lms/shared-types'
import { apiRequest } from './client'

const halfDaySlotSchema = z.object({
  date: z.string().date(),
  slot: z.enum(['AM', 'PM']),
})

const leaveDraftSchema = z.object({
  id: z.guid(),
  type: z.string() as z.ZodType<LeaveTypeId>,
  startDate: z.string().date(),
  endDate: z.string().date(),
  halfDay: z.boolean(),
  halfStart: z.boolean(),
  halfEnd: z.boolean(),
  isEmergency: z.boolean(),
  reason: z.string(),
  savedAt: z.string(),
  // Documents attached to the draft (uploaded during Save-draft), with
  // their persisted OCR verdicts — used to re-hydrate the file + OCR on
  // restore. Defaulted so an older API response without the field parses.
  documents: z.array(docRefSchema).default([]),
})

export type LeaveDraft = z.infer<typeof leaveDraftSchema>

export interface LeaveDraftUpsert {
  type: LeaveTypeId
  startDate: string
  endDate: string
  halfDay?: boolean
  halfStart?: boolean
  halfEnd?: boolean
  halfDays?: ReadonlyArray<{ date: string; slot: 'AM' | 'PM' }>
  isEmergency?: boolean
  reason?: string
}

export async function getLeaveDraft(): Promise<LeaveDraft | null> {
  const data = await apiRequest<LeaveDraft | null>('/api/v1/me/leave-draft', {
    method: 'GET',
  })
  if (data === null) return null
  return leaveDraftSchema.parse(data)
}

export async function upsertLeaveDraft(input: LeaveDraftUpsert): Promise<LeaveDraft> {
  const data = await apiRequest<LeaveDraft>('/api/v1/me/leave-draft', {
    method: 'PUT',
    body: input,
  })
  return leaveDraftSchema.parse(data)
}

export async function deleteLeaveDraft(): Promise<void> {
  await apiRequest<void>('/api/v1/me/leave-draft', { method: 'DELETE' })
}
